import { useState } from 'react';
import { toast } from 'sonner';
import { MoreVertical, Ban, Undo2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { useConfirm } from '../ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { api, errMsg, eur } from './api';

export const MOTIFS = [['doublon', 'Doublon'], ['inutile', 'Document inutile'], ['erreur_scan', 'Erreur de scan'], ['illisible', 'Document illisible'], ['autre', 'Autre']];
const PENDING = ['en_attente', 'a_verifier', 'doublon_detecte', 'deja_scannee'];

function EcartDialog({ ids, onClose, onDone }) {
    const [motif, setMotif] = useState('');
    const [com, setCom] = useState('');
    const [busy, setBusy] = useState(false);
    const go = async () => {
        setBusy(true);
        try {
            const r = await api.dismiss(ids, motif, com);
            if (r.ecartees.length) toast.success(`${r.ecartees.length} facture(s) écartée(s) — aucune dépense ni retenue`);
            r.refusees.forEach(x => toast.error(`Non écartée : ${x.raison}`));
            onDone(r.ecartees);
        } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
    };
    return (
        <Dialog open onOpenChange={o => !o && onClose()}>
            <DialogContent className="max-w-md bg-white" data-testid="ecart-dialog">
                <DialogHeader><DialogTitle>Écarter {ids.length > 1 ? `${ids.length} factures` : 'cette facture'}</DialogTitle></DialogHeader>
                <p className="text-[12px] text-gray-500">La facture quitte la liste « En attente », reste consultable dans « Écartées » et peut être restaurée. Aucune dépense ni retenue n'est créée.</p>
                <div className="grid grid-cols-2 gap-2" data-testid="ecart-motifs">
                    {MOTIFS.map(([k, l]) => (
                        <button key={k} onClick={() => setMotif(k)} data-testid={`ecart-motif-${k}`}
                            className={`rounded-lg border px-3 py-2 text-[13px] text-left transition-colors ${motif === k ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 hover:border-gray-400'}`}>{l}</button>
                    ))}
                </div>
                <Textarea value={com} onChange={e => setCom(e.target.value)} placeholder={motif === 'autre' ? 'Précisez le motif (obligatoire)' : 'Commentaire (facultatif)'} className="text-[13px] min-h-[60px]" data-testid="ecart-comment" />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button disabled={busy || !motif || (motif === 'autre' && !com.trim())} onClick={go} className="bg-gray-900 text-white hover:bg-gray-800" data-testid="ecart-submit">Écarter</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// Actions de nettoyage partagées (liste, sélection multiple, fiche ouverte)
export function useInvoiceCleanup(onChanged) {
    const { user } = useAuth();
    const [ids, setIds] = useState(null);
    const [confirm, confirmDialog] = useConfirm();
    const canManage = user && user.role !== 'assistant';
    const isSuper = user?.role === 'super_admin';
    const restore = async (list) => {
        try {
            const r = await api.restoreMany(list);
            if (r.restaurees.length) toast.success(`${r.restaurees.length} facture(s) restaurée(s) en attente`);
            r.refusees.forEach(x => toast.error(x.raison));
            onChanged(r.restaurees, 'restore');
        } catch (e) { toast.error(errMsg(e)); }
    };
    const purge = async (it) => {
        if (!(await confirm(`Supprimer DÉFINITIVEMENT la facture ${it.fournisseur || ''} n°${it.numero_facture || '—'} (${eur(it.montant_ttc)}) ?\n\nUne copie de traçabilité est conservée. Le fichier scanné est effacé s'il n'est plus utilisé par aucune autre ligne ni dossier. Action irréversible.`, { title: 'Suppression définitive', confirmLabel: 'Supprimer définitivement', danger: true }))) return;
        try { const r = await api.deleteForever(it.id); toast.success(`Facture supprimée — fichier ${r.fichier}`); onChanged([it.id], 'purge'); } catch (e) { toast.error(errMsg(e)); }
    };
    const dialogs = (
        <>
            {confirmDialog}
            {ids && <EcartDialog ids={ids} onClose={() => setIds(null)} onDone={done => { setIds(null); onChanged(done, 'dismiss'); }} />}
        </>
    );
    return { canManage, isSuper, dismiss: setIds, restore, purge, dialogs };
}

export function InvoiceRowMenu({ it, c, testId }) {
    if (!c.canManage) return null;
    const pending = PENDING.includes(it.statut);
    const out = ['supprime', 'archive'].includes(it.statut);
    if (!pending && !out) return null;
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button onClick={e => e.stopPropagation()} className="p-1 rounded-md text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors" aria-label="Actions" data-testid={testId || `invoice-menu-${it.id}`}><MoreVertical className="w-4 h-4" /></button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={e => e.stopPropagation()}>
                {pending && <DropdownMenuItem onSelect={() => c.dismiss([it.id])} data-testid={`invoice-menu-dismiss-${it.id}`}><Ban className="w-4 h-4 mr-2" />Écarter cette facture…</DropdownMenuItem>}
                {out && <DropdownMenuItem onSelect={() => c.restore([it.id])} data-testid={`invoice-menu-restore-${it.id}`}><Undo2 className="w-4 h-4 mr-2" />Restaurer</DropdownMenuItem>}
                {out && it.statut === 'supprime' && c.isSuper && <DropdownMenuItem onSelect={() => c.purge(it)} className="text-red-600" data-testid={`invoice-menu-purge-${it.id}`}><Trash2 className="w-4 h-4 mr-2" />Supprimer définitivement</DropdownMenuItem>}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
